import { escape } from 'lodash';
import { HealthIssueResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { Injectable } from '@angular/core';

export interface ContextualizedHealthIssue extends HealthIssueResponse {
    targetName: string;
    title: string;
    description: string;
    remedy: string;
    sourceName?: string;
}

export interface EntityTypeMapper {
    [key: string]: (issue: HealthIssueResponse) => { key: string; value: any };
}

@Injectable()
export class HealthIssueService {
    
    constructor(
        private admin: AdminEditorService
    ) { }

    public readonly ENTITIES_TRANSLATE_KEY = 'ADMIN.EDITORS.HEALTHISSUE.ENTITIES';
    public readonly BASE_TRANSLATE_KEY = 'ADMIN.EDITORS.HEALTHISSUE.CONTEXT';

    contextualizeIssue(issue: HealthIssueResponse): ContextualizedHealthIssue {
        let key = `${this.BASE_TRANSLATE_KEY}.${issue.source.type}.${issue.target.type}.${issue.topic}`;
        if (issue.target.id === issue.source.id) {
            key = `${this.BASE_TRANSLATE_KEY}.${issue.target.type}.${issue.topic}`;
        }

        // the dashboards produce a whole lot of different keys that require the same translations
        key = key.toUpperCase().replace(/\.(DASHBOARD|ANALYTICSBASEREPORT\.DASHBOARD)/, '.ANALYTICSDASHBOARD');

        // Some time ago, all email related failures were handled as `ADMIN.EDITORS.HEALTHISSUE.CONTEXT.EMAILSERVER....`
        // Then we introduced emailProviders, but weren't able to completely move away from the vendor level emailServer settings in place of only emailProviders
        // We had to fake vendor-level emailServer settings as an EmailProvider, which resulted in us producing errors at `.VENDOR.` level, and `.EMAILPROVIDER.` level
        // This means we'd need to duplicate our translations for `.EMAILSERVER.` to `.VENDOR.` AND `.EMAILPROVIDER.`
        // Instead, we are replacing these 2 values with `.EMAILSERVER.` so that we do not need to duplicate the keys

        // tslint:disable-next-line: no-shadowed-variable
        const tryReplaceTranslationKey = (key: string, value: any): string => {
            key = this.sanitizeTranslationKey(key);

            const translated = this.translate(key, value);
            if (translated !== key) {
                // the key has been translated, return the translated value
                return translated;
            }

            const newKey = key.replace(/\.(VENDOR|EMAILPROVIDER)/, '.EMAILSERVER');
            if (newKey === key) {
                // the regex could not find anything to replace in the key, so there is no
                // need to run a translation on this key which is still the same as it was
                // originally received.
                return translated;
            }

            // no translation was found, now try with the replacement value
            const translatedWithReplacement = this.translate(newKey, value);
            if (newKey !== translatedWithReplacement) {
                // it was able to translate the replaced key, return that value
                return translatedWithReplacement;
            }

            // no translation could be found with the replaced key, just return the original translation value
            return translated;
        };

        const result = {
            ...issue,
            targetName: tryReplaceTranslationKey(`${this.ENTITIES_TRANSLATE_KEY}.${issue.target.type}`, { name: issue.target.name}),
            title: tryReplaceTranslationKey(`${key}.TITLE`, issue),
            description: tryReplaceTranslationKey(`${key}.DESCRIPTION`, issue),
            remedy: tryReplaceTranslationKey(`${key}.REMEDY`, issue),
            sourceName: tryReplaceTranslationKey(`${this.ENTITIES_TRANSLATE_KEY}.${issue.source.type}`, { name: issue.source.name}),
        };

        const isSystemUser = this.admin.user.owner.type === 'system';
        const hasUntranslatedValues = !!Object.values(result).find(value => {
            if (typeof value  !== 'string') {
                return false;
            }

            return value.includes(this.BASE_TRANSLATE_KEY) || value.includes(this.ENTITIES_TRANSLATE_KEY);
        });

        if (!isSystemUser && hasUntranslatedValues) {
            return undefined;
        }

        if (issue.target.id === issue.source.id) {
            result.source = undefined;
            result.sourceName = undefined;
        }

        return result;
    }

    translate(key: string, value?: any): any {
        return escape(this.admin.i18n.instant(this.sanitizeTranslationKey(key), value));
    }

    sanitizeTranslationKey (key: string): string {
        return key && key.replace(/\s|-/g, '_').toUpperCase();
    }


    async getIssuesForEntity(ownerId: string, entityId: string, max: number): Promise<ContextualizedHealthIssue[]> {
        try {
            const issues = await this.admin.getCached(`listIssues/${ownerId}/${entityId}/false/0/${max}`, api => api.health.listIssues(ownerId, entityId, false, 0, max));
            return issues.items.map(issue => this.contextualizeIssue(issue)).filter(x => !!x);
        } catch (err) {
            console.error(err);
            return [];
        }
    }


}
